import { useState, useCallback } from "react";
import { ScrollView, Pressable, View, Text } from "react-native";
import { MyFeesSkeleton } from "@/components/Skeleton";
import { useFocusEffect } from "expo-router";
import styled from "styled-components/native";
import {
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { useRouter } from "expo-router";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import type { Match, Fee } from "@/lib/types";

/* ── styled ──────────────────────────────────────────── */

const PayAllBtn = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.card}px;
  padding: 16px;
  align-items: center;
  margin: 20px 20px 8px;
`;

const PayAllBtnText = styled.Text`
  color: #fff;
  font-size: 16px;
  font-weight: ${font.bold};
`;

const SectionLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 20px 20px 8px;
`;

const ListCard = styled.View`
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  margin-horizontal: 20px;
  overflow: hidden;
`;

const Row = styled.View<{ hasBorder?: boolean }>`
  flex-direction: row;
  align-items: center;
  padding: 14px 16px;
  ${(p) =>
    p.hasBorder &&
    `border-bottom-width: 1px; border-bottom-color: ${colors.line};`}
`;

const RowMain = styled.View`
  flex: 1;
  margin-right: 12px;
`;

const RowTitle = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const RowSub = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  margin-top: 2px;
`;

const PaySmallBtn = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.md}px;
  padding: 8px 14px;
`;

const PaySmallBtnText = styled.Text`
  color: #fff;
  font-size: 13px;
  font-weight: ${font.bold};
`;

const PaidAmount = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  text-decoration-line: line-through;
`;

const PaidCheck = styled.View`
  width: 22px;
  height: 22px;
  border-radius: 11px;
  background-color: ${colors.brand};
  align-items: center;
  justify-content: center;
  margin-left: 8px;
`;

const LoadingContainer = styled.View`
  flex: 1;
  background-color: ${colors.bg};
  justify-content: center;
  align-items: center;
`;

const EmptyText = styled.Text`
  font-size: 14px;
  color: ${colors.inkMuted};
  text-align: center;
  padding: 24px;
`;

const ProgressBarOuter = styled.View`
  height: 8px;
  background-color: ${colors.line};
  border-radius: ${radius.full}px;
  margin-top: 8px;
  overflow: hidden;
`;

const ProgressBarInner = styled.View<{ pct: number }>`
  height: 100%;
  width: ${(p) => Math.min(p.pct, 100)}%;
  background-color: ${colors.brand};
  border-radius: ${radius.full}px;
`;

/* ── helpers ─────────────────────────────────────────── */

function pence(amount: number) {
  return `£${(amount / 100).toFixed(2)}`;
}

function formatDate(dateStr: string) {
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

interface MatchFee {
  matchId: string;
  matchName: string;
  matchDate: string;
  fee: Fee;
}

interface Membership {
  totalAmount: number;
  installments: number;
  installmentAmount: number;
  paidAmount: number;
  payments: { amount: number; paidAt: any; stripePaymentIntentId?: string }[];
}

/* ── screen ──────────────────────────────────────────── */

export default function MyFeesScreen() {
  const router = useRouter();
  const { user } = useAuth();
  const { club, membership: clubMembership } = useClub();
  const isJunior = clubMembership?.isJunior || false;

  const [loading, setLoading] = useState(true);
  const [matchFees, setMatchFees] = useState<MatchFee[]>([]);
  const [trainingFees, setTrainingFees] = useState<MatchFee[]>([]);
  const [membership, setMembership] = useState<Membership | null>(null);

  const loadFees = useCallback(async () => {
    if (!club?.id || !user?.uid) return;
    setLoading(true);
    const clubId = club.id;
    const userId = user.uid;

    // Single query for ALL user's fees across matches and trainings
    const feeSnap = await getDocs(
      query(
        collectionGroup(db, "fees"),
        where("clubId", "==", clubId),
        where("userId", "==", userId),
      ),
    );

    const matchFeesList: MatchFee[] = [];
    const trainingFeesList: MatchFee[] = [];

    feeSnap.docs.forEach((d) => {
      const data = d.data() as any;
      const feeType = data.feeType || "match";
      const item: MatchFee = {
        matchId: d.ref.parent.parent?.id || "",
        matchName: data.matchName || "Unknown",
        matchDate: data.matchDate || "",
        fee: { id: d.id, ...data } as any,
      };
      if (feeType === "training") {
        trainingFeesList.push(item);
      } else {
        matchFeesList.push(item);
      }
    });

    const sortFees = (a: MatchFee, b: MatchFee) => {
      if (a.fee.paid !== b.fee.paid) return a.fee.paid ? 1 : -1;
      return b.matchDate.localeCompare(a.matchDate);
    };

    matchFeesList.sort(sortFees);
    trainingFeesList.sort(sortFees);
    setMatchFees(matchFeesList);
    setTrainingFees(trainingFeesList);

    const memberRef = doc(db, "clubs", clubId, "members", userId);
    const memberSnap = await getDoc(memberRef);
    if (memberSnap.exists()) {
      const data = memberSnap.data();
      if (data.membership) {
        setMembership(data.membership as Membership);
      }
    }

    setLoading(false);
  }, [club?.id, user?.uid]);

  useFocusEffect(
    useCallback(() => {
      loadFees();
    }, [loadFees]),
  );

  if (loading) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.bg }}>
        <MyFeesSkeleton />
      </View>
    );
  }

  const unpaidMatchFees = matchFees.filter((mf) => !mf.fee.paid);
  const paidMatchFees = matchFees.filter((mf) => mf.fee.paid);

  const unpaidMatchTotal = unpaidMatchFees.reduce(
    (sum, mf) => sum + mf.fee.amount,
    0,
  );

  const membershipRemaining = membership
    ? membership.totalAmount - membership.paidAmount
    : 0;
  const membershipFullyPaid = membership
    ? membership.paidAmount >= membership.totalAmount
    : true;

  const totalOutstanding =
    unpaidMatchTotal + (membershipFullyPaid ? 0 : membershipRemaining);
  const hasOutstanding = totalOutstanding > 0;

  const handlePayAll = () => {
    const parts: string[] = [];
    if (unpaidMatchTotal > 0)
      parts.push(
        `${unpaidMatchFees.length} match fee${unpaidMatchFees.length > 1 ? "s" : ""}`,
      );
    if (!membershipFullyPaid) parts.push("membership");
    const description = `All outstanding: ${parts.join(" + ")}`;
    router.push(
      `/(app)/pay?amount=${totalOutstanding}&description=${encodeURIComponent(description)}&feeType=bulk`,
    );
  };

  const handlePayMatchFee = (mf: MatchFee) => {
    router.push(
      `/(app)/pay?amount=${mf.fee.amount}&description=${encodeURIComponent(mf.matchName)}&matchId=${mf.matchId}&feeId=${user!.uid}&feeType=match_fee`,
    );
  };

  const handlePayMembership = () => {
    if (!membership) return;
    const nextAmount = Math.min(
      membership.installmentAmount,
      membershipRemaining,
    );
    router.push(
      `/(app)/pay?amount=${nextAmount}&description=${encodeURIComponent("Membership installment")}&feeId=${user!.uid}&feeType=membership`,
    );
  };

  // Group fees into a single card per section using a section-aware renderer.
  const renderSection = (
    label: string,
    fees: MatchFee[],
    keyPrefix: string,
  ) => {
    if (fees.length === 0) return null;
    return (
      <View key={keyPrefix}>
        <SectionLabel>{label}</SectionLabel>
        <ListCard>
          {fees.map((mf, i) => {
            const isLast = i === fees.length - 1;
            const isPaid = mf.fee.paid;
            return (
              <Row key={mf.matchId} hasBorder={!isLast}>
                <RowMain>
                  <RowTitle>{mf.matchName}</RowTitle>
                  <RowSub>{formatDate(mf.matchDate)}</RowSub>
                </RowMain>
                {isPaid ? (
                  <View style={{ flexDirection: "row", alignItems: "center" }}>
                    <PaidAmount>{pence(mf.fee.amount)}</PaidAmount>
                    <PaidCheck>
                      <Text
                        style={{
                          color: "#fff",
                          fontSize: 12,
                          fontWeight: "700",
                        }}
                      >
                        ✓
                      </Text>
                    </PaidCheck>
                  </View>
                ) : (
                  <Pressable
                    onPress={() => handlePayMatchFee(mf)}
                    style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })}
                  >
                    <PaySmallBtn>
                      <PaySmallBtnText>
                        Pay {pence(mf.fee.amount)}
                      </PaySmallBtnText>
                    </PaySmallBtn>
                  </Pressable>
                )}
              </Row>
            );
          })}
        </ListCard>
      </View>
    );
  };

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: colors.bg }}
      showsVerticalScrollIndicator={false}
    >
      <ContentWidth>
        {hasOutstanding && !isJunior && (
          <Pressable
            onPress={handlePayAll}
            style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })}
          >
            <PayAllBtn>
              <PayAllBtnText>
                Pay all outstanding {pence(totalOutstanding)}
              </PayAllBtnText>
            </PayAllBtn>
          </Pressable>
        )}
        {hasOutstanding && isJunior && (
          <View
            style={{
              margin: 20,
              padding: 14,
              backgroundColor: colors.amber50,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: colors.amber200,
            }}
          >
            <Text
              style={{
                fontSize: 14,
                fontWeight: "700",
                color: colors.amber,
                textAlign: "center",
              }}
            >
              Your guardian will receive payment requests
            </Text>
          </View>
        )}

        {membership && (
          <View>
            <SectionLabel>Membership</SectionLabel>
            <ListCard>
              <View style={{ padding: 16 }}>
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <RowTitle>Annual Membership</RowTitle>
                  {membershipFullyPaid ? (
                    <PaidCheck>
                      <Text
                        style={{
                          color: "#fff",
                          fontSize: 12,
                          fontWeight: "700",
                        }}
                      >
                        ✓
                      </Text>
                    </PaidCheck>
                  ) : null}
                </View>
                <ProgressBarOuter>
                  <ProgressBarInner
                    pct={(membership.paidAmount / membership.totalAmount) * 100}
                  />
                </ProgressBarOuter>
                <RowSub style={{ marginTop: 6 }}>
                  {pence(membership.paidAmount)} of{" "}
                  {pence(membership.totalAmount)} paid
                </RowSub>
                {!membershipFullyPaid && (
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: 12,
                    }}
                  >
                    <Text style={{ fontSize: 13, color: colors.inkMuted }}>
                      Next:{" "}
                      {pence(
                        Math.min(
                          membership.installmentAmount,
                          membershipRemaining,
                        ),
                      )}
                    </Text>
                    <Pressable
                      onPress={handlePayMembership}
                      style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })}
                    >
                      <PaySmallBtn>
                        <PaySmallBtnText>Pay</PaySmallBtnText>
                      </PaySmallBtn>
                    </Pressable>
                  </View>
                )}
              </View>
            </ListCard>
          </View>
        )}

        {renderSection("Unpaid match fees", unpaidMatchFees, "unpaid-match")}
        {renderSection(
          "Unpaid training",
          trainingFees.filter((f) => !f.fee.paid),
          "unpaid-training",
        )}
        {renderSection("Paid match fees", paidMatchFees, "paid-match")}
        {renderSection(
          "Paid training",
          trainingFees.filter((f) => f.fee.paid),
          "paid-training",
        )}

        {matchFees.length === 0 && trainingFees.length === 0 && !membership && (
          <EmptyText>No fees to show yet.</EmptyText>
        )}

        <View style={{ height: 40 }} />
      </ContentWidth>
    </ScrollView>
  );
}
